import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { BarChart3 } from "lucide-react";

interface ThemePerformance {
  theme: string;
  postCount: number;
  avgEngagement: number;
  trend: 'up' | 'down' | 'stable';
}

interface ContentPerformanceChartProps {
  themePerformance: ThemePerformance[];
}

export const ContentPerformanceChart = ({ themePerformance }: ContentPerformanceChartProps) => {
  const chartData = themePerformance.map(item => ({
    theme: item.theme.replace(/([A-Z])/g, ' $1').trim(),
    'Post Count': item.postCount,
    'Avg Engagement': item.avgEngagement,
  }));

  const chartConfig = {
    'Post Count': {
      label: 'Post Count',
      color: 'hsl(var(--primary))',
    },
    'Avg Engagement': {
      label: 'Avg Engagement',
      color: 'hsl(var(--success))',
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Content Theme Performance
        </CardTitle>
        <CardDescription>
          Analysis of content themes by post count and average engagement
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="theme" 
                className="text-xs"
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis className="text-xs" />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              <Bar 
                dataKey="Post Count" 
                fill="var(--color-Post Count)" 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="Avg Engagement" 
                fill="var(--color-Avg Engagement)" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {themePerformance.slice(0, 2).map((theme) => (
            <div key={theme.theme} className="p-3 bg-muted/50 rounded-lg">
              <h4 className="font-semibold text-sm mb-1">
                {theme.theme.replace(/([A-Z])/g, ' $1').trim()}
              </h4>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{theme.postCount} posts</span>
                <span className="text-primary font-medium">
                  {theme.avgEngagement} avg engagement
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
